import { useState, useEffect, useCallback, useRef } from "react";
import {
  AdminEntriesService,
  AdminEntriesResponse,
  TimeEntryWithUser,
  SortField,
  SortOrder,
} from "../services/adminEntries";
import { timeTrackingService } from "../services/timeTracking";
import { User } from "../../../shared/types";
import { RoleManagementService } from "../services/roleManagement";

export type { SortField, SortOrder, TimeEntryWithUser };

interface UseAdminEntriesParams {
  initialPageSize?: number;
}

export function useAdminEntries(params: UseAdminEntriesParams = {}) {
  const { initialPageSize = 25 } = params;

  // State
  const [entries, setEntries] = useState<TimeEntryWithUser[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);

  // Filter and sort state
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  // Prevent duplicate fetches
  const isFetchingRef = useRef(false);

  // Fetch users (for the filter dropdown)
  const fetchUsers = useCallback(async () => {
    try {
      const response = await RoleManagementService.getAllUsers(1, 1000);
      setUsers(response.users);
    } catch (err) {
      console.error("Failed to fetch users:", err);
    }
  }, []);

  // Main fetch function
  const fetchEntries = useCallback(async () => {
    if (isFetchingRef.current) return;

    try {
      isFetchingRef.current = true;
      setLoading(true);
      setError(null);

      const response: AdminEntriesResponse = await AdminEntriesService.getEntries({
        page,
        pageSize,
        sortField,
        sortOrder,
        userId: selectedUserId || undefined,
      });

      setEntries(response.entries);
      setTotalCount(response.pagination.totalCount);
      setTotalPages(response.pagination.totalPages);
      setHasNextPage(response.pagination.hasNextPage);
      setHasPrevPage(response.pagination.hasPrevPage);
    } catch (err) {
      console.error("Error fetching admin entries:", err);
      const errorMessage =
        err instanceof Error ? err.message : "Failed to fetch time entries";
      setError(errorMessage);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [page, pageSize, sortField, sortOrder, selectedUserId]);

  // Fetch entries when params change
  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Fetch users on mount
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Handle page change
  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  // Handle page size change
  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page
  }, []);

  // Handle user filter change
  const handleUserChange = useCallback((userId: string) => {
    setSelectedUserId(userId);
    setPage(1); // Reset to first page
  }, []);

  // Handle sort change
  const handleSort = useCallback(
    (field: SortField) => {
      if (field === sortField) {
        // Toggle direction
        setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        // New field, default to descending
        setSortField(field);
        setSortOrder("desc");
      }
      setPage(1); // Reset to first page
    },
    [sortField]
  );

  // Get current duration for running entry
  const getCurrentDuration = useCallback((entry: TimeEntryWithUser) => {
    if (entry.endTime && entry.duration) {
      return entry.duration;
    } else {
      const start = new Date(entry.startTime).getTime();
      const end = Date.now();
      return Math.floor((end - start) / 1000);
    }
  }, []);

  // Delete entry
  const deleteEntry = useCallback(
    async (id: string) => {
      try {
        await timeTrackingService.deleteTimeEntry(id);
        await fetchEntries(); // Refresh the list
      } catch (err) {
        console.error("Error deleting time entry:", err);
        const errorMessage =
          err instanceof Error ? err.message : "Failed to delete time entry";
        setError(errorMessage);
        throw err;
      }
    },
    [fetchEntries]
  );

  // Refresh
  const refresh = useCallback(() => {
    fetchEntries();
  }, [fetchEntries]);

  return {
    // Data
    entries,
    users,
    loading,
    error,

    // Pagination
    pagination: {
      page,
      pageSize,
      totalCount,
      totalPages,
      hasNextPage,
      hasPrevPage,
    },

    // Filter and sort
    selectedUserId,
    sortField,
    sortOrder,

    // Actions
    handlePageChange,
    handlePageSizeChange,
    handleUserChange,
    handleSort,
    getCurrentDuration,
    deleteEntry,
    refresh,
  };
}
